// Persistencia local (localStorage) pra:
//   - Presets/templates: combinacoes nomeadas de (destino + periodo +
//     variaveis) que o operador salva pra reusar.
//   - Historico recente: ultimos N relatorios gerados (auto-salvo).
//
// Tudo client-only — nao precisa de backend. Se o usuario limpar o
// browser cache, perde os dados (aceitavel pra MVP). Quando houver
// backend de presets, basta substituir as funcoes abaixo por chamadas
// API e a UI continua identica.

const PRESETS_KEY = 'higra:relatorios:presets';
const HISTORY_KEY = 'higra:relatorios:history';
const HISTORY_MAX = 10;

// ===== Helpers =====

const safeParse = (json, fallback) => {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const readArray = (key) => {
  if (typeof window === 'undefined') return [];
  return safeParse(window.localStorage.getItem(key), []);
};

const writeArray = (key, array) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(array));
  } catch {
    // localStorage cheio ou bloqueado — silencioso, melhor que crash
  }
};

const uuid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// ===== Presets =====
//
// Shape de um preset:
// {
//   id: string,
//   name: string,
//   createdAt: number (ms),
//   destination: {
//     clienteId, clienteLabel,
//     aplicacaoId, aplicacaoLabel,
//     thingId, thingLabel
//   } | null,
//   periodPresetKey: '7d' | '30d' | 'currentMonth' | 'previousMonth' | 'custom',
//   variableIds: string[],   // ids de variaveis OU 'setpoint-X' ids
// }

export const listPresets = () => {
  const arr = readArray(PRESETS_KEY);
  // ordena mais recente primeiro pra UI render
  return [...arr].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

export const savePreset = (preset) => {
  const list = readArray(PRESETS_KEY);
  const withId = {
    ...preset,
    id: preset.id || uuid(),
    createdAt: preset.createdAt || Date.now(),
  };
  // Se ja existe preset com o mesmo nome, substituimos (UPSERT) — operador
  // raramente quer dois 'Mensal SABESP' diferentes.
  const filtered = list.filter(
    (p) => p.name.trim().toLowerCase() !== withId.name.trim().toLowerCase()
  );
  filtered.push(withId);
  writeArray(PRESETS_KEY, filtered);
  return withId;
};

export const deletePreset = (presetId) => {
  const list = readArray(PRESETS_KEY);
  writeArray(
    PRESETS_KEY,
    list.filter((p) => p.id !== presetId)
  );
};

// ===== Historico =====
//
// Shape de um item:
// {
//   id: string,
//   generatedAt: number (ms),
//   thingLabel: string,
//   aplicacaoLabel: string | null,
//   clienteLabel: string | null,
//   periodLabel: string,         // ex.: '08/05 — 16/05'
//   variableCount: number,
//   // dados pra regerar (mesmas chaves dos presets)
//   destination,
//   periodPresetKey,
//   variableIds,
// }

export const listHistory = () => {
  const arr = readArray(HISTORY_KEY);
  return [...arr].sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0));
};

export const recordHistory = (entry) => {
  const list = readArray(HISTORY_KEY);
  const withMeta = {
    ...entry,
    id: entry.id || uuid(),
    generatedAt: entry.generatedAt || Date.now(),
  };
  // Mantem ate HISTORY_MAX itens — descartar mais antigos no overflow.
  const next = [withMeta, ...list].slice(0, HISTORY_MAX);
  writeArray(HISTORY_KEY, next);
  return withMeta;
};

export const clearHistory = () => writeArray(HISTORY_KEY, []);
